import { useEffect, useState } from 'react';
import {
  availableCreditPaise,
  limitAtDate,
  outstandingPaise,
  utilisationPercent,
  type Account,
  type LimitChange,
} from '../../core/accounts';
import { formatDateKey, todayKey } from '../../core/dates';
import { formatPaise, parseAmount } from '../../core/money';
import { AccountError, changeCreditLimit, watchAccounts, watchLimitHistory } from '../../data/accounts';
import type { UserRecord } from '../../data/auth';

interface Props {
  record: UserRecord;
}

export function CardsScreen({ record }: Props) {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchAccounts(setAccounts), []);

  if (accounts === null) return <p className="muted">Loading cards…</p>;

  const cards = accounts.filter((a) => a.type === 'credit_card' && !a.archived);
  if (cards.length === 0) {
    return (
      <>
        <h1>Cards</h1>
        <div className="placeholder">No cards yet. Seed accounts first.</div>
      </>
    );
  }

  const totalOutstanding = cards.reduce((s, c) => s + outstandingPaise(c.openingBalancePaise), 0);
  const totalLimit = cards.reduce((s, c) => s + (c.creditLimitPaise ?? 0), 0);

  return (
    <>
      <h1>Cards</h1>
      <p className="muted">
        {cards.length} cards · outstanding <strong>{formatPaise(totalOutstanding)}</strong> of{' '}
        {formatPaise(totalLimit)} · {utilisationPercent(-totalOutstanding, totalLimit)}% overall
      </p>
      {error && <p className="error">{error}</p>}

      {cards.map((card) => (
        <CardPanel key={card.id} card={card} record={record} onError={setError} />
      ))}
    </>
  );
}

function CardPanel({
  card,
  record,
  onError,
}: {
  card: Account;
  record: UserRecord;
  onError: (message: string | null) => void;
}) {
  const [history, setHistory] = useState<LimitChange[]>([]);
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => watchLimitHistory(card.id, setHistory), [card.id]);

  const outstanding = outstandingPaise(card.openingBalancePaise);
  const limit = card.creditLimitPaise ?? 0;
  const utilisation = utilisationPercent(card.openingBalancePaise, limit);
  const available = availableCreditPaise(card.openingBalancePaise, limit);

  const dueLabel = card.dueDayIsNextMonth
    ? `${ordinal(card.dueDay ?? 1)} of the following month`
    : ordinal(card.dueDay ?? 1);

  return (
    <section className="panel">
      <div className="head-row">
        <h2>{card.name}</h2>
        <span className="muted small">
          {card.statementDay == null
            ? 'No statement date'
            : `Statement ${ordinal(card.statementDay)}`}{' '}
          · Due {dueLabel}
        </span>
      </div>

      <div
        className="meter"
        role="meter"
        aria-valuenow={utilisation}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${card.name} utilisation`}
      >
        <div
          className="meter-fill"
          data-level={utilisation >= 90 ? 'high' : utilisation >= 60 ? 'mid' : 'low'}
          style={{ width: `${Math.min(utilisation, 100)}%` }}
        />
      </div>

      <table className="table compact">
        <tbody>
          <tr>
            <td>Outstanding</td>
            <td className="num">{formatPaise(outstanding)}</td>
          </tr>
          <tr>
            <td>Available</td>
            <td className="num">{formatPaise(available)}</td>
          </tr>
          <tr>
            <td>Limit</td>
            <td className="num">
              {formatPaise(limit)}
              {history.length > 1 && (
                <div className="muted tiny">{history.length} limit changes</div>
              )}
            </td>
          </tr>
          <tr>
            <td>Utilisation</td>
            <td className="num">{utilisation}%</td>
          </tr>
        </tbody>
      </table>

      {card.note && <p className="muted tiny">{card.note}</p>}

      <div className="row">
        <button type="button" className="btn-link" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Cancel' : 'Change limit'}
        </button>
        {history.length > 0 && (
          <button type="button" className="btn-link" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? 'Hide' : 'Show'} limit history
          </button>
        )}
        {/* Bill payment is a transfer (FR-CARD-03), so it needs the transaction form
            from M3. Named here rather than omitted, because it is the commonest card
            action and the one most likely to be miscategorised as an expense. */}
        <span className="muted tiny">Pay bill arrives with transactions at M3</span>
      </div>

      {editing && (
        <LimitForm
          card={card}
          onCancel={() => setEditing(false)}
          onSave={async (limitPaise, effectiveFrom) => {
            onError(null);
            try {
              await changeCreditLimit(card, limitPaise, effectiveFrom, record.uid);
              setEditing(false);
            } catch (cause) {
              onError(cause instanceof AccountError ? cause.message : 'Could not change the limit.');
            }
          }}
        />
      )}

      {showHistory && (
        <table className="table compact">
          <tbody>
            {[...history]
              .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))
              .map((change) => (
                <tr key={`${change.effectiveFrom}-${change.limitPaise}`}>
                  <td>{formatDateKey(change.effectiveFrom, 'dd MMM yyyy')}</td>
                  <td className="num">{formatPaise(change.limitPaise)}</td>
                  <td className="num muted tiny">
                    {utilisationPercent(card.openingBalancePaise, change.limitPaise)}% at
                    today’s balance
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}

      {showHistory && history.length > 1 && (
        <p className="muted tiny">
          Utilisation for a past month uses the limit in force then — on{' '}
          {formatDateKey(history[0].effectiveFrom, 'dd MMM yyyy')} that was{' '}
          {formatPaise(limitAtDate(history, history[0].effectiveFrom))}.
        </p>
      )}
    </section>
  );
}

function LimitForm({
  card,
  onCancel,
  onSave,
}: {
  card: Account;
  onCancel: () => void;
  onSave: (limitPaise: number, effectiveFrom: string) => void;
}) {
  const [value, setValue] = useState(String((card.creditLimitPaise ?? 0) / 100));
  const [from, setFrom] = useState(todayKey());
  const parsed = parseAmount(value);

  return (
    <div className="subpanel">
      <label className="field">
        <span>New limit</span>
        <input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" autoFocus />
        <small className={parsed === null ? 'error' : 'muted'}>
          {parsed === null ? 'Not a valid amount' : formatPaise(parsed)}
        </small>
      </label>
      <label className="field">
        <span>Effective from</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <small className="muted">
          Recorded as a dated change, not an overwrite — utilisation before this date keeps
          using the old limit.
        </small>
      </label>
      <div className="row">
        <button
          type="button"
          className="btn btn-primary"
          disabled={parsed === null}
          onClick={() => parsed !== null && onSave(parsed, from)}
        >
          Save limit
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ordinal(day: number): string {
  const suffix =
    day % 10 === 1 && day !== 11 ? 'st'
    : day % 10 === 2 && day !== 12 ? 'nd'
    : day % 10 === 3 && day !== 13 ? 'rd'
    : 'th';
  return `${day}${suffix}`;
}
